import { onLCP, onINP, onCLS, onFCP, onTTFB, Metric } from 'web-vitals';

export interface PerformanceMetrics {
  lcp?: number; // Largest Contentful Paint
  inp?: number; // Interaction to Next Paint (replaces FID)
  cls?: number; // Cumulative Layout Shift
  fcp?: number; // First Contentful Paint
  ttfb?: number; // Time to First Byte
  tti?: number; // Time to Interactive
}

export interface PerformanceThresholds {
  lcp: { good: number; needsImprovement: number };
  inp: { good: number; needsImprovement: number };
  cls: { good: number; needsImprovement: number };
}

export interface PerformanceMetricData {
  breeder_id?: string;
  metric_type: string;
  metric_value: number;
  page_url: string;
  device_type: string;
  connection_type: string;
  timestamp: number;
}

export const PERFORMANCE_THRESHOLDS: PerformanceThresholds = {
  lcp: { good: 2500, needsImprovement: 4000 },
  inp: { good: 200, needsImprovement: 500 },
  cls: { good: 0.1, needsImprovement: 0.25 },
};

class PerformanceMonitor {
  private metrics: PerformanceMetrics = {};
  private breederId?: string;
  private onMetricCallback?: (metric: PerformanceMetricData) => void;

  constructor(breederId?: string, onMetricCallback?: (metric: PerformanceMetricData) => void) {
    this.breederId = breederId;
    this.onMetricCallback = onMetricCallback;
  }

  /**
   * Initialize all performance observers
   */
  public initializeObservers(): void {
    this.measureLCP();
    this.measureINP();
    this.measureCLS();
    this.measureFCP();
    this.measureTTFB();
    this.measureTTI();
  }

  /**
   * Measure Largest Contentful Paint (LCP)
   */
  public measureLCP(): void {
    onLCP((metric: Metric) => {
      this.metrics.lcp = metric.value;
      this.handleMetric('lcp', metric);
    });
  }

  /**
   * Measure Interaction to Next Paint (INP) - replaces FID
   */
  public measureINP(): void {
    onINP((metric: Metric) => {
      this.metrics.inp = metric.value;
      this.handleMetric('inp', metric);
    });
  }

  /**
   * Measure Cumulative Layout Shift (CLS)
   */
  public measureCLS(): void {
    onCLS((metric: Metric) => {
      this.metrics.cls = metric.value;
      this.handleMetric('cls', metric);
    });
  }

  /**
   * Measure First Contentful Paint (FCP)
   */
  public measureFCP(): void {
    onFCP((metric: Metric) => {
      this.metrics.fcp = metric.value;
      this.handleMetric('fcp', metric);
    });
  }

  /**
   * Measure Time to First Byte (TTFB)
   */
  public measureTTFB(): void {
    onTTFB((metric: Metric) => {
      this.metrics.ttfb = metric.value;
      this.handleMetric('ttfb', metric);
    });
  }

  /**
   * Measure Time to Interactive (TTI)
   * Using PerformanceObserver for long tasks
   */
  public measureTTI(): void {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          
          // Calculate TTI based on when there are no long tasks
          const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
          if (navigationEntry) {
            const tti = navigationEntry.domInteractive;
            this.metrics.tti = tti;
            
            if (this.onMetricCallback) {
              this.onMetricCallback({
                breeder_id: this.breederId,
                metric_type: 'tti',
                metric_value: tti,
                page_url: window.location.pathname,
                device_type: this.getDeviceType(),
                connection_type: this.getConnectionType(),
                timestamp: Date.now(),
              });
            }
          }
        });

        observer.observe({ entryTypes: ['longtask', 'navigation'] });
      } catch (error) {
        console.warn('TTI measurement not supported:', error);
      }
    }
  }

  /**
   * Handle metric collection and callback
   */
  private handleMetric(metricType: string, metric: Metric): void {
    if (this.onMetricCallback) {
      this.onMetricCallback({
        breeder_id: this.breederId,
        metric_type: metricType,
        metric_value: metric.value,
        page_url: window.location.pathname,
        device_type: this.getDeviceType(),
        connection_type: this.getConnectionType(),
        timestamp: Date.now(),
      });
    }
  }

  /**
   * Get all collected metrics
   */
  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  /**
   * Get metric score (good, needs-improvement, poor)
   */
  public getMetricScore(metricType: keyof PerformanceThresholds, value: number): 'good' | 'needs-improvement' | 'poor' {
    const thresholds = PERFORMANCE_THRESHOLDS[metricType];
    if (!thresholds) return 'good';

    if (value <= thresholds.good) return 'good';
    if (value <= thresholds.needsImprovement) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Send metrics to analytics endpoint
   */
  public async sendToAnalytics(): Promise<void> {
    const metricsToSend: PerformanceMetricData[] = [];

    Object.entries(this.metrics).forEach(([type, value]) => {
      if (value !== undefined) {
        metricsToSend.push({
          breeder_id: this.breederId,
          metric_type: type,
          metric_value: value,
          page_url: window.location.pathname,
          device_type: this.getDeviceType(),
          connection_type: this.getConnectionType(),
          timestamp: Date.now(),
        });
      }
    });

    if (metricsToSend.length === 0) return;

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/collect-performance-metrics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ metrics: metricsToSend }),
      });

      if (!response.ok) {
        throw new Error(`Failed to send metrics: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to send performance metrics:', error);
    }
  }

  /**
   * Get device type
   */
  private getDeviceType(): string {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * Get connection type
   */
  private getConnectionType(): string {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    if (connection) {
      return connection.effectiveType || 'unknown';
    }
    
    return 'unknown';
  }

  /**
   * Get optimization suggestions based on metrics
   */
  public getOptimizationSuggestions(): string[] {
    const suggestions: string[] = [];

    if (this.metrics.lcp && this.metrics.lcp > PERFORMANCE_THRESHOLDS.lcp.good) {
      suggestions.push('Optimize Largest Contentful Paint: Consider lazy loading images, reducing server response time, or optimizing critical rendering path.');
    }

    if (this.metrics.inp && this.metrics.inp > PERFORMANCE_THRESHOLDS.inp.good) {
      suggestions.push('Improve Interaction to Next Paint: Reduce JavaScript execution time, break up long tasks, or use web workers for heavy computations.');
    }

    if (this.metrics.cls && this.metrics.cls > PERFORMANCE_THRESHOLDS.cls.good) {
      suggestions.push('Fix Cumulative Layout Shift: Add size attributes to images and videos, avoid inserting content above existing content, or use CSS aspect ratio boxes.');
    }

    if (this.metrics.fcp && this.metrics.fcp > 1800) {
      suggestions.push('Improve First Contentful Paint: Eliminate render-blocking resources, minify CSS, or inline critical CSS.');
    }

    if (this.metrics.ttfb && this.metrics.ttfb > 600) {
      suggestions.push('Reduce Time to First Byte: Optimize server response time, use a CDN, or implement caching strategies.');
    }

    return suggestions;
  }
}

// Export singleton instance
let performanceMonitorInstance: PerformanceMonitor | null = null;

export const initPerformanceMonitoring = (
  breederId?: string,
  onMetricCallback?: (metric: PerformanceMetricData) => void
): PerformanceMonitor => {
  if (!performanceMonitorInstance) {
    performanceMonitorInstance = new PerformanceMonitor(breederId, onMetricCallback);
    performanceMonitorInstance.initializeObservers();
  }
  return performanceMonitorInstance;
};

export const getPerformanceMonitor = (): PerformanceMonitor | null => {
  return performanceMonitorInstance;
};

export default PerformanceMonitor;
